import React from 'react';
import { 
    Chip, 
    Tooltip, 
    Typography, 
    CircularProgress,
    makeStyles 
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    // Floating indicator wrapper - positioned absolutely so it doesn't affect layout
    floatingIndicatorWrapper: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: theme.spacing(1),
        height: 28,
        width: 28,
        zIndex: 10
    },
    // Compact chip - icon only by default, expands LEFT on hover
    compactChip: {
        position: 'absolute',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        transformOrigin: 'right center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        cursor: 'default',
        // Icon-only state
        minWidth: 28,
        width: 28,
        padding: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& .MuiChip-label': {
            opacity: 0,
            width: 0,
            padding: 0,
            color: '#ffffff',
            transition: 'opacity 0.2s ease-in-out, width 0.2s ease-in-out, padding 0.2s ease-in-out'
        },
        '& .MuiChip-icon': {
            margin: 0,
            color: '#ffffff'
        },
        // Hover state - expand LEFT to show label
        '&:hover': {
            width: 'auto',
            minWidth: 'auto',
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            '& .MuiChip-label': {
                opacity: 1,
                width: 'auto',
                paddingLeft: theme.spacing(0.5),
                paddingRight: theme.spacing(0.5),
                color: '#ffffff'
            },
            '& .MuiChip-icon': {
                marginRight: theme.spacing(0.5),
                color: '#ffffff'
            }
        }
    },
    chip: {
        marginLeft: theme.spacing(1)
    },
    warningChip: {
        backgroundColor: theme.palette.warning.dark,
        color: '#ffffff',
        '& .MuiChip-icon': {
            color: '#ffffff'
        }
    },
    errorChip: {
        backgroundColor: theme.palette.error.dark,
        color: '#ffffff',
        '& .MuiChip-icon': {
            color: '#ffffff'
        }
    },
    successChip: {
        backgroundColor: theme.palette.success.main,
        color: '#ffffff',
        '& .MuiChip-icon': {
            color: '#ffffff'
        }
    },
    // Loading spinner - compact
    loadingSpinner: {
        marginLeft: theme.spacing(1)
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    tooltipContent: {
        maxWidth: 300
    },
    missingList: {
        margin: 0,
        paddingLeft: theme.spacing(2),
        fontSize: '0.85rem'
    },
    menuItemDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
        '& .MuiTypography-root': {
            color: theme.palette.text.disabled
        }
    },
    menuItemWarning: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
    },
    unavailableIndicator: {
        marginLeft: 'auto',
        fontSize: '0.75rem',
        color: theme.palette.warning.dark,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5)
    }
}));

/**
 * ZoneAvailabilityIndicator
 * 
 * Shows a compact floating indicator next to the zone dropdown.
 * Displays only an icon by default, expands on hover to show status text.
 * Uses absolute positioning so it floats over adjacent form fields without pushing them.
 */
export const ZoneAvailabilityIndicator = ({ 
    isLoading, 
    isAvailable, 
    missingInstances = [],
    error,
    unableToVerify
}) => {
    const classes = useStyles();

    if (isLoading) {
        return (
            <span className={classes.floatingIndicatorWrapper}>
                <CircularProgress size={18} className={classes.loadingSpinner} />
            </span>
        );
    }

    // Unable to verify state (e.g., auth failures, API errors)
    if (unableToVerify) {
        return (
            <span className={classes.floatingIndicatorWrapper}>
                <Tooltip title="Unable to verify instance availability. This may be due to authentication issues or API errors.">
                    <Chip
                        icon={<WarningIcon style={{ fontSize: 16 }} />}
                        label="Unable to verify"
                        size="small"
                        className={classes.compactChip + ' ' + classes.warningChip}
                    />
                </Tooltip>
            </span>
        );
    }

    if (error) {
        return (
            <span className={classes.floatingIndicatorWrapper}>
                <Tooltip title={'Error checking availability: ' + error}>
                    <Chip
                        icon={<ErrorIcon style={{ fontSize: 16 }} />}
                        label="Check failed"
                        size="small"
                        className={classes.compactChip + ' ' + classes.warningChip}
                    />
                </Tooltip>
            </span>
        );
    }

    if (isAvailable === false) {
        var tooltipContent = (
            <div className={classes.tooltipContent}>
                <Typography variant="body2" gutterBottom>
                    The following instances are not available in this zone:
                </Typography>
                <ul className={classes.missingList}>
                    {missingInstances.map(function(inst, idx) {
                        return <li key={idx}>{inst}</li>;
                    })}
                </ul>
            </div>
        );

        return (
            <span className={classes.floatingIndicatorWrapper}>
                <Tooltip title={tooltipContent}>
                    <Chip
                        icon={<WarningIcon style={{ fontSize: 16 }} />}
                        label={missingInstances.length + ' unavailable'}
                        size="small"
                        className={classes.compactChip + ' ' + classes.errorChip}
                    />
                </Tooltip>
            </span>
        );
    }

    if (isAvailable === true) {
        return (
            <span className={classes.floatingIndicatorWrapper}>
                <Tooltip title="All selected instances are available in this zone">
                    <Chip
                        icon={<CheckCircleIcon style={{ fontSize: 16 }} />}
                        label="All instances available"
                        size="small"
                        className={classes.compactChip + ' ' + classes.successChip}
                    />
                </Tooltip>
            </span>
        );
    }

    return null;
};

/**
 * ZoneMenuItemContent
 * 
 * Renders zone name with availability warning if applicable
 * Use this inside MenuItem components
 */
export const ZoneMenuItemContent = ({
    zoneName,
    zoneId,
    isAvailable,
    missingInstances = [],
    showDetails = false
}) => {
    const classes = useStyles();

    if (isAvailable !== false) {
        return <span>{zoneName}</span>;
    }

    const tooltipContent = showDetails ? (
        <div className={classes.tooltipContent}>
            <Typography variant="body2" gutterBottom>
                Not all selected instances are available:
            </Typography>
            <ul className={classes.missingList}>
                {missingInstances.map((inst, idx) => (
                    <li key={idx}>{inst}</li>
                ))}
            </ul>
        </div>
    ) : `${missingInstances.length} selected instance(s) not available in this zone`;

    return (
        <Tooltip title={tooltipContent} placement="right">
            <div className={classes.menuItemWarning}>
                <span>{zoneName}</span>
                <span className={classes.unavailableIndicator}>
                    <WarningIcon fontSize="small" />
                    <span>Limited</span>
                </span>
            </div>
        </Tooltip>
    );
};

/**
 * InstanceAvailabilityBanner
 * 
 * A banner to show at the top of the form when there are availability issues
 */
export const InstanceAvailabilityBanner = ({
    unavailableZones = [],
    selectedZone,
    isZoneAvailable,
    getMissingInstances,
    isLoading
}) => {
    const classes = useStyles();

    if (isLoading) {
        return null;
    }

    // Only show banner if selected zone has issues
    if (!selectedZone || isZoneAvailable(selectedZone)) {
        return null;
    }

    const missingInstances = getMissingInstances(selectedZone);

    return (
        <div style={{
            padding: '12px 16px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: 4,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12
        }}>
            <WarningIcon style={{ color: '#856404', marginTop: 2 }} />
            <div>
                <Typography variant="body1" style={{ color: '#856404', fontWeight: 500 }}>
                    Instance Availability Warning
                </Typography>
                <Typography variant="body2" style={{ color: '#856404' }}>
                    The following instances are not available in zone {selectedZone}:
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: 20, color: '#856404' }}>
                    {missingInstances.map((inst, idx) => (
                        <li key={idx}><strong>{inst}</strong></li>
                    ))}
                </ul>
                <Typography variant="body2" style={{ color: '#856404' }}>
                    Please select a different zone or change your instance selections.
                </Typography>
            </div>
        </div>
    );
};

/**
 * Helper function to get zone disabled state for dropdown
 */
export const getZoneDisabledState = (zoneId, isZoneAvailable, allowOverride = false) => {
    // If allowOverride is true, don't disable but show warning
    if (allowOverride) {
        return false;
    }
    return !isZoneAvailable(zoneId);
};

/**
 * Helper function to format zone label with availability info
 */
export const formatZoneLabel = (zone, isZoneAvailable, getMissingInstances) => {
    const isAvailable = isZoneAvailable(zone.zone_id);
    const name = zone.name || zone.zone_id;
    
    if (isAvailable) {
        return name;
    }
    
    const missing = getMissingInstances(zone.zone_id);
    if (missing.length > 0) {
        return `${name} (${missing.length} instance(s) unavailable)`;
    }
    
    return `${name} (Some instances unavailable)`;
};

export default {
    ZoneAvailabilityIndicator,
    ZoneMenuItemContent,
    InstanceAvailabilityBanner,
    getZoneDisabledState,
    formatZoneLabel
};
